/* eslint-disable @typescript-eslint/explicit-function-return-type */
import uniq from 'lodash/uniq';
import uniqBy from 'lodash/uniqBy';
import {
  ResourceFilterBarSelectOptions,
  ResourceFilterBarFormState,
  DefaultOption
} from '../types';
import { Resource } from 'types/schema-generated-types';

export const FAVORITES = 'favorites';
export const FAVORITES_PLAINTEXT = 'Liked Resources';

export const getAssessmentAcceleratorTitle = (productCodeOrGrade: string) => {
  let grade = '(ERROR)';
  switch (productCodeOrGrade) {
    case '3':
    case 'KEY3':
      grade = '3rd';
      break;
    case '4':
    case 'KEY4':
      grade = '4th';
      break;
    case '5':
    case 'KEY5':
      grade = '5th';
      break;
  }
  return `${grade} Grade Student Assessment Accelerator Previews`;
};

export const isAll = (value: string) => value === DefaultOption.ALL;
export const isFavorites = (value: string | number) => value === FAVORITES;

export const getResourceFavorite = (resourceFavorites: Resource[], entity: Resource) => {
  return resourceFavorites.find((y: Resource) => entity.id === y.id) || null;
};

export const withFavorite = (resourceFavorites: Resource[], resources: Resource[]) => {
  return resources.map(x => ({ ...x, favorite: getResourceFavorite(resourceFavorites, x) }));
};

export const getSearchBarSelectOptions = (
  resourceTypeId: number | string,
  resources: Resource[],
  { t } = { t: (value: string) => value }
): ResourceFilterBarSelectOptions => {
  return {
    resourceOptions: isFavorites(resourceTypeId)
      ? uniqBy(
          resources.map(x => ({ display: x.type.title, value: x.type.type })),
          'value'
        )
      : [{ display: t('Liked Resources'), value: FAVORITES }],
    productOptions: uniq(resources.map(x => x.productFamily?.name).filter(x => x)) as string[],
    gradeOptions: uniq(
      resources.reduce(
        (acc: string[], x: Resource) => [
          ...acc,
          ...(Array.isArray(x.grades) ? (x.grades as string[]) : [])
        ],
        []
      )
    )
  };
};

export const filterResources = (
  { resource, product, grade, search }: ResourceFilterBarFormState,
  resources: Resource[],
  resourceFavorites: Resource[]
) => {
  if (isAll(resource) && isAll(product) && isAll(grade) && !search) return resources;

  const toLower = (value = '') => value.toLowerCase();
  const isFuzzy = (value1: string | null | undefined, value2: string) =>
    toLower(value1 ?? '').includes(toLower(value2));
  const isResourceOrFavoriteMatch = (value: string, x: Resource) =>
    isAll(value) ||
    (isFavorites(value) ? getResourceFavorite(resourceFavorites, x) : value === x.type.type);
  const isProductMatch = (value: string, x: Resource) =>
    isAll(value) || value === x.productFamily?.name;
  const isGradeMatch = (value: string, x: Resource) => isAll(value) || x.grades?.includes(value);
  const isSearchFuzzyMatch = (value: string, x: Resource) =>
    value
      ? isFuzzy(x.title, search) ||
        isFuzzy(x.description, search) ||
        isFuzzy(x.productFamily?.name, search) ||
        isFuzzy(x.grades?.join(','), search)
      : true;

  return resources.filter(
    x =>
      isResourceOrFavoriteMatch(resource, x) &&
      isProductMatch(product, x) &&
      isGradeMatch(grade, x) &&
      isSearchFuzzyMatch(search, x)
  );
};
